import { useEffect, useState, useRef } from 'react'
import { Box, Typography, Chip, Avatar, Tooltip, useTheme, useMediaQuery, IconButton } from '@mui/material'
import Person from '@mui/icons-material/Person'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Close from '@mui/icons-material/Close'
import { getDataWithParams } from 'src/consumer/services/profileService'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { styled, keyframes } from '@mui/material/styles'
import dayjs from 'dayjs'

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
`

const flashAnimation = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  25% {
    opacity: 0.7;
    transform: scale(0.98);
  }
  50% {
    opacity: 1;
    transform: scale(1.02);
  }
  75% {
    opacity: 0.7;
    transform: scale(0.98);
  }
`

const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile' && prop !== 'isVisible' && prop !== 'isFlashing',
})(({ theme, isMobile, isVisible, isFlashing }) => ({
  position: 'fixed',
  bottom: isMobile ? 'auto' : '24px',
  top: isMobile ? '80px' : 'auto', // Position at top on mobile, accounting for header
  right: isMobile ? '12px' : '24px',
  left: isMobile ? '12px' : 'auto',
  zIndex: 1200,
  minWidth: isMobile ? '280px' : '320px',
  maxWidth: isMobile ? 'calc(100vw - 24px)' : '380px',
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  borderRadius: '16px',
  border: '2px solid rgba(16, 185, 129, 0.2)',
  boxShadow: 
    '0 8px 32px rgba(16, 185, 129, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(16, 185, 129, 0.1)',
  padding: '16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateY(0)' : 'translateY(-20px)',
  pointerEvents: isVisible ? 'auto' : 'none',
  animation: isFlashing && isMobile ? `${flashAnimation} 0.5s ease-in-out 3` : 'none',
  '&:hover': {
    boxShadow: 
      '0 12px 40px rgba(16, 185, 129, 0.2), 0 6px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(16, 185, 129, 0.15)',
    transform: isMobile ? 'translateY(0)' : 'translateY(-2px)',
  },
  [theme.breakpoints.down('md')]: {
    padding: '12px',
    borderRadius: '12px',
  },
}))

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
  paddingBottom: '12px',
  borderBottom: '1px solid rgba(16, 185, 129, 0.15)',
}))

const CountBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '32px',
  height: '32px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.25) 100%)',
  border: '2px solid rgba(16, 185, 129, 0.3)',
  color: 'rgba(16, 185, 129, 1)',
  fontWeight: 700,
  fontSize: '14px',
  position: 'relative',
  animation: `${pulseAnimation} 2s infinite`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    background: 'rgba(16, 185, 129, 0.1)',
    animation: `${pulseAnimation} 2s infinite`,
    animationDelay: '0.5s',
  },
}))

const TherapistItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 12px',
  borderRadius: '10px',
  background: 'rgba(16, 185, 129, 0.04)',
  border: '1px solid rgba(16, 185, 129, 0.1)',
  marginBottom: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(16, 185, 129, 0.08)',
    borderColor: 'rgba(16, 185, 129, 0.2)',
    transform: 'translateX(4px)',
  },
  '&:last-child': {
    marginBottom: 0,
  },
  [theme.breakpoints.down('md')]: {
    padding: '8px 10px',
    gap: '10px',
  },
}))

const RoomChip = styled(Chip)(({ theme }) => ({
  height: '24px',
  fontSize: '11px',
  fontWeight: 500,
  background: 'rgba(16, 185, 129, 0.1)',
  color: 'rgba(16, 185, 129, 0.9)',
  border: '1px solid rgba(16, 185, 129, 0.2)',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}))

const STORAGE_KEY_SHOWN_STAFF = 'availableStaffCard_shownStaffIds'
const STORAGE_KEY_LAST_DATE = 'availableStaffCard_lastDate'

export const CheckedInTherapistsCard = ({
  apiSignature = '/api/secure/doctor/get-active-staff-details',
  dataRefreshFrequencySeconds = 60,
}) => {
  const [checkedInScreeners, setCheckedInScreeners] = useState([])
  const [checkedInTherapists, setCheckedInTherapists] = useState([])
  const [isVisible, setIsVisible] = useState(true)
  const [isFlashing, setIsFlashing] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const previousStaffIdsRef = useRef(new Set())
  const hasShownInitialRef = useRef(false)
  const timeoutRef = useRef(null)

  // Check if date has changed (new day) and reset tracking
  const checkAndResetDailyTracking = () => {
    const today = dayjs().format('YYYY-MM-DD')
    const lastDate = localStorage.getItem(STORAGE_KEY_LAST_DATE)
    
    if (lastDate !== today) {
      // New day - reset tracking
      localStorage.removeItem(STORAGE_KEY_SHOWN_STAFF)
      localStorage.setItem(STORAGE_KEY_LAST_DATE, today)
      previousStaffIdsRef.current = new Set()
      hasShownInitialRef.current = false
      return true // Indicates it's a new day
    }
    return false
  }

  // Load previously shown staff IDs from localStorage
  const loadShownStaffIds = () => {
    const stored = localStorage.getItem(STORAGE_KEY_SHOWN_STAFF)
    if (stored) {
      try {
        return new Set(JSON.parse(stored))
      } catch (e) {
        return new Set()
      }
    }
    return new Set()
  }

  // Save shown staff IDs to localStorage
  const saveShownStaffIds = (staffIds) => {
    localStorage.setItem(STORAGE_KEY_SHOWN_STAFF, JSON.stringify(Array.from(staffIds)))
  }

  async function getAndSetData() {
    if (getAllRolesInBoolean().isDoctor) {
      try {
        const response = await getDataWithParams(apiSignature, {})
        if (
          response?.status === 200 &&
          isCommonResponseSuccessfulV2(response) &&
          Array.isArray(response?.data?.data?.checkedInScreeners) &&
          Array.isArray(response?.data?.data?.checkedInTherapists)
        ) {
          setCheckedInScreeners(response?.data?.data?.checkedInScreeners || [])
          setCheckedInTherapists(response?.data?.data?.checkedInTherapists || [])
        }
      } catch (error) {
        console.error('Error fetching active staff details:', error)
      }
    }
  }

  // Initialize daily tracking on mount
  useEffect(() => {
    checkAndResetDailyTracking()
    previousStaffIdsRef.current = loadShownStaffIds()
  }, [])

  useEffect(() => {
    getAndSetData() // Initial fetch

    const intervalId = setInterval(() => {
      getAndSetData()
    }, dataRefreshFrequencySeconds * 1000)

    return () => clearInterval(intervalId) // Cleanup on unmount
  }, [apiSignature, dataRefreshFrequencySeconds])

  const allStaff = removeDuplicatesFromStaffList([...checkedInScreeners, ...checkedInTherapists])
  const currentStaffIds = new Set(allStaff.map(staff => staff.teacherId))

  // Check for new staff members
  const hasNewStaff = () => {
    if (currentStaffIds.size === 0) return false

    // Check if there are any new staff IDs not in previousStaffIdsRef
    for (const staffId of currentStaffIds) {
      if (!previousStaffIdsRef.current.has(staffId)) {
        return true // Found a new staff member
      }
    }
    return false // No new staff members
  }

  // Check for new staff and show card accordingly (both mobile and desktop)
  useEffect(() => {
    if (allStaff.length > 0) {
      // Check if it's a new day first
      const isNewDay = checkAndResetDailyTracking()
      if (isNewDay) {
        // New day - reset tracking and show all current staff
        previousStaffIdsRef.current = new Set()
        hasShownInitialRef.current = false
      }

      const hasNew = hasNewStaff()
      
      // Only show if it's the first time OR there's a new staff member OR it's a new day
      if (!hasShownInitialRef.current || hasNew || isNewDay) {
        // Update shown staff IDs
        const updatedShownIds = new Set(previousStaffIdsRef.current)
        currentStaffIds.forEach(id => updatedShownIds.add(id))
        previousStaffIdsRef.current = updatedShownIds
        saveShownStaffIds(updatedShownIds)
        
        // Show the card
        setIsVisible(true)
        hasShownInitialRef.current = true
        
        // Mobile-specific: flash animation
        if (isMobile) {
          // Start flashing
          setIsFlashing(true)
          
          // Stop flashing after animation completes (3 iterations * 0.5s = 1.5s)
          const flashTimer = setTimeout(() => {
            setIsFlashing(false)
          }, 1500)

          return () => {
            clearTimeout(flashTimer)
          }
        }
      }
    }
  }, [isMobile, allStaff.length, checkedInScreeners, checkedInTherapists])
  
  // Set timeout whenever popup becomes visible
  useEffect(() => {
    if (isVisible && allStaff.length > 0) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      
      // Set 30-second timeout to close the popup
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false)
      }, 30000)
      
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
      }
    }
  }, [isVisible, allStaff.length])

  // Don't show if no staff is checked in
  if (allStaff?.length === 0) {
    return null
  }

  const totalCount = allStaff.length

  const handleClose = () => {
    setIsVisible(false)
  }

  return (
    <CardContainer 
      className="hide-on-print-only"
      isMobile={isMobile}
      isVisible={isVisible}
      isFlashing={isFlashing}
    >
      <HeaderBox>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircle 
            sx={{ 
              fontSize: 24, 
              color: 'rgba(16, 185, 129, 0.9)',
              filter: 'drop-shadow(0 2px 4px rgba(16, 185, 129, 0.3))',
            }} 
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? '15px' : '16px',
              color: 'rgba(15, 23, 42, 0.95)',
              letterSpacing: '-0.01em',
            }}
          >
            Available Staff
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CountBadge>
            {totalCount}
          </CountBadge>
          {isMobile && (
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                width: '28px',
                height: '28px',
                color: 'rgba(15, 23, 42, 0.6)',
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.08)',
                  color: 'rgba(15, 23, 42, 0.9)',
                },
              }}
            >
              <Close sx={{ fontSize: '18px' }} />
            </IconButton>
          )}
        </Box>
      </HeaderBox>

      <Box sx={{ maxHeight: isMobile ? '300px' : '400px', overflowY: 'auto', pr: 0.5 }}>
        {allStaff.map((staff, idx) => (
          <TherapistItem key={`${staff.teacherId}-${idx}`}>
            <Avatar
              sx={{
                width: isMobile ? 36 : 40,
                height: isMobile ? 36 : 40,
                bgcolor: 'rgba(16, 185, 129, 0.15)',
                color: 'rgba(16, 185, 129, 0.9)',
                fontWeight: 600,
                fontSize: isMobile ? '14px' : '16px',
                border: '2px solid rgba(16, 185, 129, 0.2)',
              }}
            >
              {staff?.teacherName?.charAt(0)?.toUpperCase() || <Person />}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '13px' : '14px',
                  color: 'rgba(15, 23, 42, 0.95)',
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {staff?.teacherName || 'Unknown'}
              </Typography>
              {staff?.chamberRoomName && (
                <RoomChip 
                  label={staff.chamberRoomName}
                  size="small"
                />
              )}
            </Box>
          </TherapistItem>
        ))}
      </Box>
    </CardContainer>
  )
}

function removeDuplicatesFromStaffList(arr) {
  const seenTeacherIds = new Set()

  return arr.filter((item) => {
    if (seenTeacherIds.has(item.teacherId)) {
      return false
    } else {
      seenTeacherIds.add(item.teacherId)
      return true
    }
  })
}

